/**
 * api-fetch/boards — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  Card,
  CardAiThread,
  CardAttachment,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  Notebook,
  NotebookActionItemT,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  BoardActivityPage,
  BoardRestoreApplyResult,
  BoardRestorePlan,
  BoardRoutingRule,
  BoardRoutingRuleCreate,
  KanbanApi,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  KanbanPresenceHeartbeatResult,
  PromoteLaneToBoardResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  MeetingAnalyzeResult,
  MeetingLivePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import { BoardListSchema, BoardSchema, LaneListSchema, validateOrWarn } from '../validation'
import type {
  BoardAccessRoster,
  BoardCategoryGrantResult,
  BoardCategoryRoster,
  BoardExclusionResult,
} from '../api/payloads-category-access'
import type { ApiFetchContext } from './context'

export function createBoardsApi(ctx: ApiFetchContext) {
  const {
    _request, _trelloRequest, _casHeaders, _siblingRequest, accessApiBase,
  } = ctx
  return {
    // Boards ----------------------------------------------------------
    // Arc 11 / audit P2-12 — wrap the raw JSON in a runtime
    // schema check so a deploy-skew between FE expectations and
    // BE serializer surface emits a Sentry warning + console
    // breadcrumb instead of cascading into a render-time
    // TypeError. Validation is best-effort; the value is returned
    // as-is regardless of result so a missing optional field
    // doesn't white-screen the operator.
    listBoards: async (filter?: BoardListFilter) => {
      // `includeArchived: true` widens the result set to BOTH
      // active + archived (BE: `?include_archived=true`, added in
      // quotech-backend MR !2995). When set we must suppress the
      // binary `archived` filter because the BE prefers the
      // narrower `archived=true`/`false` whenever it's present;
      // passing both would re-binarise the response and undo the
      // collapse the rail relies on.
      const includeArchived = filter?.includeArchived === true
      const raw = await _request<unknown>(
        'GET', '/boards/', undefined, {
          kind: filter?.kind,
          archived: includeArchived ? undefined : filter?.archived,
          include_archived: includeArchived ? 'true' : undefined,
          job: filter?.jobId,
          organisation: filter?.organisationId,
          writable: filter?.writable ? '1' : undefined,
        },
      )
      // BUG-2026-06-14-KANBAN-SUBBOARD-VANISH (pagination guard) — DRF's
      // default pagination returns `{count, next, previous, results}`
      // for large querysets and degrades to a bare array for small
      // ones. The boards viewset currently returns a bare array (a
      // 159-board workspace renders today), but if pagination is ever
      // switched on, returning `raw` as-is would leave consumers with a
      // non-array object and silently drop EVERY board — including the
      // parents that sub-boards nest under. Mirror the unwrap that
      // `fetchTrashedBoards` already does so the rail/gallery stay
      // resilient, and warn loudly if a truncating `next` cursor shows
      // up so we notice rather than silently losing boards past page 1.
      let value: Board[]
      if (Array.isArray(raw)) {
        value = raw as Board[]
      } else {
        const page = raw as { results?: Board[]; next?: string | null }
        value = page?.results ?? []
        if (page?.next) {
          console.warn(
            'GET /boards/ returned a paginated response with a `next` ' +
              'cursor — the board list is truncated to the first page. ' +
              'Sub-boards whose parent is on a later page will not nest. ' +
              'Disable pagination on the boards viewset or add cursor ' +
              'following here.',
          )
        }
      }
      validateOrWarn(BoardListSchema, value, 'GET /boards/')
      return value
    },
    getBoard: async (id, opts) => {
      const value = await _request<Board>(
        'GET',
        `/boards/${id}/`,
        undefined,
        undefined,
        undefined,
        { signal: opts?.signal },
      )
      validateOrWarn(BoardSchema, value, `GET /boards/${id}/`)
      return value
    },
    getBoardSnapshot: (boardId, opts) =>
      _request<BoardSnapshot>(
        'GET',
        `/boards/${boardId}/snapshot/`,
        undefined,
        {
          per_lane_limit:
            opts?.perLaneLimit === undefined ? undefined : String(opts.perLaneLimit),
        },
        undefined,
        { signal: opts?.signal },
      ),
    createBoard: (input: BoardCreate) =>
      _request<Board>('POST', '/boards/', input),
    updateBoard: (id, patch) =>
      _request<Board>('PATCH', `/boards/${id}/`, patch),
    deleteBoard: async (id: string) => {
      // Soft-delete — qb!3057 turned the BE's `perform_destroy` into
      // a `deleted_at = now()` stamp with a 30-day grace window.
      // Cascade-hide is at the queryset level (lane / card joins).
      // The daily `purge_kanban_soft_deleted` job physically removes
      // rows after 30 days. Host shows a typed-confirmation before
      // calling; the modal copy mentions the grace window so the
      // operator knows the action is reversible within 30 days.
      // ARC-9 D3 — deleting a parent board now returns 200 with
      // ``{ rerooted_children }`` (the sub-boards promoted to top
      // level); a childless board keeps the plain 204 (undefined).
      return await _request<
        { rerooted_children?: Array<{ id: string; name: string }> } | void
      >('DELETE', `/boards/${id}/`)
    },
    // BUG-2026-05-24-KANBAN-PIN-LOCAL-ONLY — server-backed pin state.
    // GET shape: `{ board_ids: string[] }` (newest pin first).
    // PUT shape: `{ board_ids: string[] }` → echoes the canonical
    // post-write list (server-filtered through the visibility fence,
    // so the response may be a SUBSET of what we sent — we trust the
    // server's filter). See useBoardPins for the migration semantics.
    listBoardPins: async () => {
      const value = await _request<{ board_ids: string[] }>(
        'GET', '/board-pins/',
      )
      // Defensive shape check — a deploy-skew or malformed BE could
      // return null/undefined for the array. Falling back to [] keeps
      // the rail rendering instead of throwing during react-query's
      // select pipeline.
      return Array.isArray(value?.board_ids) ? value.board_ids : []
    },
    replaceBoardPins: async (boardIds: string[]) => {
      const value = await _request<{ board_ids: string[] }>(
        'PUT', '/board-pins/', { board_ids: boardIds },
      )
      return Array.isArray(value?.board_ids) ? value.board_ids : []
    },
    // FEAT-2026-05-24-KANBAN-BOARD-HOME — caller's home board pointer
    // for a given org. Query-param shape so the FE never accidentally
    // omits the org scope (the BE 400s without it).
    getBoardHome: async (orgId: number) => {
      const value = await _request<{ board_id: string | null }>(
        'GET', `/board-home/?org_id=${encodeURIComponent(String(orgId))}`,
      )
      const id = value?.board_id
      return typeof id === 'string' && id ? id : null
    },
    setBoardHome: async (orgId: number, boardId: string | null) => {
      const value = await _request<{ board_id: string | null }>(
        'PUT',
        `/board-home/?org_id=${encodeURIComponent(String(orgId))}`,
        { board_id: boardId },
      )
      const id = value?.board_id
      return typeof id === 'string' && id ? id : null
    },
    // FEAT-2026-05-25-BOARD-DEFAULTS-PER-CATEGORY — per-(category)
    // default board pointers. Backed by qb!3079's
    // /api/kanban/board-defaults/ endpoint (HOME/JOB/QUOTE/FINANCE/
    // EMAIL). The full bag is returned in one round-trip — the FE
    // hook hydrates a single react-query entry and every consumer
    // (auto-resume effect, Jobs landing, Quotes landing, Finance
    // landing, Email submenu) reads its slot without an extra fetch.
    getBoardDefaults: async (orgId: number) => {
      const value = await _request<{
        defaults: Partial<Record<BoardDefaultCategory, string | null>>
      }>(
        'GET',
        `/board-defaults/?org_id=${encodeURIComponent(String(orgId))}`,
      )
      // Defensive coercion — older servers / malformed responses
      // fall back to "every slot null" rather than throwing during
      // react-query's select pipeline. The hook will surface the
      // GET error separately via `error` if the request itself
      // failed.
      const bag = (value?.defaults ?? {}) as Partial<
        Record<BoardDefaultCategory, string | null>
      >
      return {
        HOME: typeof bag.HOME === 'string' && bag.HOME ? bag.HOME : null,
        JOB: typeof bag.JOB === 'string' && bag.JOB ? bag.JOB : null,
        QUOTE: typeof bag.QUOTE === 'string' && bag.QUOTE ? bag.QUOTE : null,
        FINANCE:
          typeof bag.FINANCE === 'string' && bag.FINANCE ? bag.FINANCE : null,
        EMAIL: typeof bag.EMAIL === 'string' && bag.EMAIL ? bag.EMAIL : null,
        // FEAT-2026-05-25-BOARD-DEFAULTS-PER-CATEGORY follow-up —
        // BUILDER is the 6th category added to the BE in migration
        // 0040_boardhome_builder_choice. Older BE bundles without
        // BUILDER simply return no row → defensive null below keeps
        // the shape stable across the deploy gap.
        BUILDER:
          typeof bag.BUILDER === 'string' && bag.BUILDER ? bag.BUILDER : null,
      }
    },
    setBoardDefault: async (
      orgId: number,
      category: BoardDefaultCategory,
      boardId: string | null,
    ) => {
      const value = await _request<{
        category: BoardDefaultCategory
        board_id: string | null
      }>(
        'PUT',
        `/board-defaults/?org_id=${encodeURIComponent(String(orgId))}` +
          `&category=${encodeURIComponent(category)}`,
        { board_id: boardId },
      )
      const id = value?.board_id
      return typeof id === 'string' && id ? id : null
    },
    restoreBoard: (id: string) =>
      _request<Board>('POST', `/boards/${id}/restore/`),
    // ARC-8 MR3 — restore-to-point. Preview is a pure dry-run; apply
    // recomputes server-side (the plan is never posted back), so a
    // stale preview can't move cards to states history never held.
    listBoardActivity: (boardId: string, opts?: { limit?: number; before?: string }) => {
      const q = new URLSearchParams()
      if (opts?.limit != null) q.set('limit', String(opts.limit))
      if (opts?.before) q.set('before', opts.before)
      const qs = q.toString()
      return _request<BoardActivityPage>(
        'GET',
        `/boards/${boardId}/activity/${qs ? `?${qs}` : ''}`,
      )
    },
    previewBoardRestore: (boardId: string, toIso: string) =>
      _request<BoardRestorePlan>(
        'POST', `/boards/${boardId}/restore/preview/`, { to: toIso },
      ),
    applyBoardRestore: (boardId: string, toIso: string) =>
      _request<BoardRestoreApplyResult>(
        'POST', `/boards/${boardId}/restore/apply/`, { to: toIso },
      ),
    fetchTrashedBoards: async () => {
      const raw = await _request<unknown>('GET', '/boards/trash/')
      // Mirror the standard list-vs-paginated unpacking used by
      // `listBoards` etc. — the DRF default paginates large queries
      // and degrades to a bare array for small ones.
      return Array.isArray(raw)
        ? (raw as Board[])
        : (((raw as { results?: Board[] })?.results) ?? [])
    },
    restoreLane: (id: string) =>
      _request<Lane>('POST', `/lanes/${id}/restore/`),
    fetchTrashedLanes: async (boardId: string) => {
      const raw = await _request<unknown>(
        'GET',
        `/lanes/trash/?board=${encodeURIComponent(boardId)}`,
      )
      return Array.isArray(raw)
        ? (raw as Lane[])
        : (((raw as { results?: Lane[] })?.results) ?? [])
    },
    restoreCard: (id: string) =>
      _request<Card>('POST', `/cards/${id}/restore/`),
    fetchTrashedCards: async (boardId: string) => {
      const raw = await _request<unknown>(
        'GET',
        `/cards/trash/?board=${encodeURIComponent(boardId)}`,
      )
      return Array.isArray(raw)
        ? (raw as Card[])
        : (((raw as { results?: Card[] })?.results) ?? [])
    },
    provisionLanes: (id) =>
      _request<Lane[]>('POST', `/boards/${id}/provision-lanes/`),
    cardCounts: (id) =>
      _request<LaneCardCount[]>('GET', `/boards/${id}/card-counts/`),
    boardSummary: (id) =>
      _request<BoardSummary>('GET', `/boards/${id}/summary/`),
    // Arc D — generic board→board routing rules. DRF list endpoint may
    // paginate; normalise both shapes to a bare array.
    listRoutingRules: async (boardId: string) => {
      const raw = await _request<
        BoardRoutingRule[] | { results: BoardRoutingRule[] }
      >('GET', '/routing-rules/', undefined, { board: boardId })
      return Array.isArray(raw) ? raw : raw?.results ?? []
    },
    createRoutingRule: (input: BoardRoutingRuleCreate) =>
      _request<BoardRoutingRule>('POST', '/routing-rules/', input),
    updateRoutingRule: (id: string, patch: Partial<BoardRoutingRuleCreate>) =>
      _request<BoardRoutingRule>('PATCH', `/routing-rules/${id}/`, patch),
    deleteRoutingRule: async (id: string) => {
      await _request<void>('DELETE', `/routing-rules/${id}/`)
    },
    // FEAT board-presence — the only presence WRITE; reads piggyback on
    // getBoardVersion's `active_viewers`. See KanbanApi interface docs.
    heartbeatBoardPresence: (boardId: string) =>
      _request<KanbanPresenceHeartbeatResult>(
        'POST', `/boards/${boardId}/presence/heartbeat/`,
      ),
    seedDefaultBoards: (input?: SeedDefaultBoardsInput) =>
      _request<SeedDefaultBoardsResult>('POST', '/boards/seed-defaults/', {
        organisation_id: input?.organisationId,
        include_jobs: input?.includeJobs ?? true,
      }),
    // TEAM board people (UAT P3-a) — see KanbanApi interface docs.
    addTeamMember: (orgId: number, input: TeamMemberAddInput) =>
      _request<TeamMemberAddResult>('POST', '/team/members/', {
        org_id: orgId,
        name: input.name,
        email: input.email || undefined,
        position: input.position || undefined,
        employment_type: input.employmentType || undefined,
      }),
    syncTeamRoster: (orgId: number) =>
      _request<TeamRosterSyncResult>('POST', '/team/sync/', { org_id: orgId }),

    // Lanes -----------------------------------------------------------
    listLanes: async (boardId: string, opts?: LaneListOptions) => {
      const value = await _request<Lane[]>('GET', '/lanes/', undefined, {
        board: boardId,
        with_counts: opts?.withCounts ? '1' : undefined,
        archived: opts?.archived,
      })
      validateOrWarn(LaneListSchema, value, `GET /lanes/?board=${boardId}`)
      return value
    },
    createLane: (input: LaneCreate) =>
      _request<Lane>('POST', '/lanes/', input),
    updateLane: (id, patch) =>
      _request<Lane>('PATCH', `/lanes/${id}/`, patch),
    reorderLanes: async (boardId: string, laneIdsInOrder: string[]) => {
      const updates = await Promise.all(
        laneIdsInOrder.map((id, idx) =>
          _request<Lane>('PATCH', `/lanes/${id}/`, { position: (idx + 1) * 10 }),
        ),
      )
      return updates
    },
    deleteLane: async (id: string) => {
      await _request<void>('DELETE', `/lanes/${id}/`)
    },

    // FEAT-2026-05-28-NEWMARK-TO-DO-DEFAULT — operator-controlled
    // override for the AI-categorised email landing rule. Server
    // unsets every sibling lane on the same board in the same
    // transaction so the flag is unique per board (enforced by
    // the partial unique constraint at the DB level).
    setLaneDefaultLanding: (laneId, opts) =>
      _request<Lane>(
        'POST',
        `/lanes/${laneId}/set-default-landing/`,
        opts ?? {},
      ),

    setLanePrimaryInbox: (laneId, opts) =>
      _request<Lane>(
        'POST',
        `/lanes/${laneId}/set-primary-inbox/`,
        opts ?? {},
      ),

    // Stack 13 MR-B — promote a lane to a sub-board. Backed by
    // POST /lanes/{id}/promote-to-board/ in qb!3049. The BE creates
    // the new Board with `parent_board = source_lane.board`,
    // provisions the kind's default lanes, and bulk-moves cards.
    // See `PromoteLaneToBoardOptions` / `PromoteLaneToBoardResult`
    // in api.ts for the payload shape.
    promoteLaneToBoard: (laneId, opts) =>
      _request<PromoteLaneToBoardResult>(
        'POST',
        `/lanes/${laneId}/promote-to-board/`,
        opts ?? {},
      ),

    setBoardParent: (boardId, parentBoardId) =>
      _request<Board>('POST', `/boards/${boardId}/set-parent/`, {
        parent_board_id: parentBoardId,
      }),

    // Lane RBAC (3d) — backed by qb!2631 endpoints.
    listLaneMembers: (laneId) =>
      _request<LaneMembership[]>('GET', `/lanes/${laneId}/members/`),
    grantLaneMember: (laneId, userId, role) =>
      _request<LaneMembership>('POST', `/lanes/${laneId}/members/`, {
        user_id: userId,
        role,
      }),
    revokeLaneMember: async (laneId, userId) => {
      await _request<void>('DELETE', `/lanes/${laneId}/members/${userId}/`)
    },

    // Board RBAC — memberships on board detail; mutations on
    // /board-memberships/ (Stack 11 PR 7, qb!2631 sibling).
    listBoardMembers: async (boardId) => {
      const board = await _request<Board>('GET', `/boards/${boardId}/`)
      return (board.memberships ?? []).filter((m) => !m.revoked_at)
    },
    grantBoardMember: (boardId, userId, role) =>
      _request<BoardMembership>('POST', '/board-memberships/', {
        board: boardId,
        user: userId,
        role: role ?? 'EDITOR',
      }),
    revokeBoardMember: async (membershipId) => {
      await _request<void>('DELETE', `/board-memberships/${membershipId}/`)
    },

    // FEAT-2026-07-27-KANBAN-CATEGORY-ACCESS — grant a whole department / kind
    // and block one person on one board. See payloads-category-access.ts.
    listBoardCategoryMembers: (scope, value, organisationId) =>
      _request<BoardCategoryRoster>(
        'GET', '/board-categories/members/', undefined,
        { organisation_id: organisationId, scope: scope.toLowerCase(), value },
      ),
    // Grant goes to the PLATFORM invite endpoint, not a kanban route (D7), so
    // it cannot use `_request` (different base). One invite surface means one
    // notification path, one audit row shape, and one oversight read.
    grantBoardCategoryMember: (input) =>
      _siblingRequest<BoardCategoryGrantResult>(
        accessApiBase, 'POST', '/invite/', {
          resource_type: 'board_category',
          resource_scope: input.scope.toLowerCase(),
          resource_id: input.value,
          organisation_id: input.organisationId,
          user_id: input.userId,
          role: input.role,
          starts_at: input.startsAt ?? null,
          expires_at: input.expiresAt ?? null,
        },
      ),
    revokeBoardCategoryMember: async (scope, value, organisationId, userId) => {
      await _request<void>(
        'DELETE', '/board-categories/members/', undefined,
        {
          organisation_id: organisationId,
          scope: scope.toLowerCase(),
          value,
          user_id: userId,
        },
      )
    },
    restrictBoardMember: (boardId, userId, reason) =>
      _request<BoardExclusionResult>(
        'POST', `/boards/${boardId}/exclusions/`,
        { user_id: userId, reason: reason ?? '' },
      ),
    unrestrictBoardMember: async (boardId, userId) => {
      await _request<void>(
        'DELETE', `/boards/${boardId}/exclusions/`, { user_id: userId },
      )
    },
    getBoardAccessRoster: (boardId) =>
      _request<BoardAccessRoster>('GET', `/boards/${boardId}/access-roster/`),

    // satisfies Partial<KanbanApi> — see api-fetch/cards.ts for the rationale
    // (back-propagates KanbanApi param types under tenant strict mode).
  } satisfies Partial<KanbanApi>
}
